"use client";

// Anonymous search token for /search and /search/autocomplete (SFDA-828).
//
// The page render mints the first token (search-token.server.ts). Tokens live about an hour
// and renew silently, so the user never sees an expiry:
// - proactively, in the background, once less than RENEW_AHEAD_MS is left (checked whenever
//   a search asks for the token and when the tab becomes visible again; no timers, since
//   background tabs throttle them);
// - reactively, when the API answers 401 (searchFetch in client-only.ts retries once);
// - as a last resort, when the API refuses to renew (too old, already used, rotated
//   secret), by re-rendering the page for a fresh token via router.refresh().
// Renewing costs a small proof-of-work, solved in a Web Worker so the UI never janks.

import { BASE_API_URL } from "./client";
import { solveRange } from "./search-token-pow";

const RENEW_AHEAD_MS = 10 * 60 * 1000;
const RETRY_DELAYS_MS = [500, 2000];
const FRESH_TOKEN_TIMEOUT_MS = 15_000;
// Re-rendering the page at most this often, so a broken setup can't loop.
const REFRESH_COOLDOWN_MS = 60_000;
const DEFAULT_POW_BITS = 17;
// Same cap as MAX_POW_BITS in apps/api/app/services/search_tokens.py.
const MAX_POW_BITS = 24;
// After a renewal fails (network, 5xx), wait this long before solving another proof-of-work,
// so a broken renew endpoint doesn't cost one per keystroke.
const RENEW_BACKOFF_MS = 30_000;

interface TokenState {
  token: string;
  acc: string;
  jti: string;
  iat: number;
  pow: number;
  expiresAt: number;
}

let state: TokenState | null = null;
// The newest token this tab holds for each account, so Back/Forward to a slug resumes its
// renewal chain instead of the page's first render token, long since renewed and consumed.
const latestByAccount = new Map<string, TokenState>();
let renewal: Promise<string | null> | null = null;
let needsFreshToken: (() => void) | null = null;
let freshTokenWaiters: Array<(next: TokenState | null) => void> = [];
let lastRefreshAt = 0;
// The token the API last refused to renew; only a page re-render can replace it.
let refusedToken: string | null = null;
let renewFailedAt = 0;
let visibilityListening = false;

/** The API refused to renew this token (401); only a page re-render can replace it. */
class RenewRefused extends Error {}

function decodeClaims(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

function toState(token: string, expiresInSeconds?: number): TokenState | null {
  const claims = decodeClaims(token);
  if (!claims || typeof claims.jti !== "string") return null;
  // The local clock times the expiry, so clock skew with the server doesn't matter.
  const iat = Number(claims.iat ?? 0);
  const ttl = expiresInSeconds ?? Number(claims.exp ?? 0) - iat;
  const pow = Number.isInteger(claims.pow) ? Number(claims.pow) : DEFAULT_POW_BITS;
  return {
    token,
    acc: String(claims.acc ?? ""),
    jti: claims.jti,
    iat,
    pow: Math.max(1, Math.min(pow, MAX_POW_BITS)),
    expiresAt: Date.now() + ttl * 1000,
  };
}

/**
 * Called by SearchProvider with the token from the page render, again after every
 * router.refresh(). `onNeedsFreshToken` should re-render the page for a new token.
 *
 * A token older than the newest one this tab holds for the same account is ignored:
 * Back/Forward restores the page with the render token it first had, which this tab has
 * since renewed (and the API has consumed).
 */
export function initSearchToken(
  token: string | null,
  onNeedsFreshToken: () => void,
) {
  needsFreshToken = onNeedsFreshToken;
  if (!token) {
    // The page has no token: SEARCH_TOKEN_SECRET isn't set (local dev, mocks).
    state = null;
    renewal = null;
  } else if (state?.token !== token) {
    const next = toState(token);
    if (next) {
      const known = latestByAccount.get(next.acc);
      // Once a token is refused, any other token is better, whatever its iat says.
      const chosen =
        known && known.token !== refusedToken && known.iat >= next.iat
          ? known
          : next;
      latestByAccount.set(chosen.acc, chosen);
      if (chosen !== state) {
        state = chosen;
        renewal = null;
        renewFailedAt = 0;
      }
    }
  }
  const waiters = freshTokenWaiters;
  freshTokenWaiters = [];
  waiters.forEach((resolve) => resolve(state));

  if (!visibilityListening && typeof document !== "undefined") {
    visibilityListening = true;
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") renewIfDue();
    });
  }
}

function renewIfDue() {
  if (state && state.expiresAt - Date.now() < RENEW_AHEAD_MS) {
    renewSearchToken().catch(() => {});
  }
}

/**
 * The token to send, or null when the page has none. Only waits when it has expired, and
 * never fails: if renewal fails the old token goes out and the API decides (in log mode it
 * still answers; in enforce mode searchFetch gets a 401 and tries to renew once more).
 */
export async function getSearchToken(): Promise<string | null> {
  if (!state) return null;
  if (Date.now() >= state.expiresAt) {
    const current = state.token;
    try {
      return (await renewSearchToken()) ?? current;
    } catch {
      return current;
    }
  }
  renewIfDue();
  return state.token;
}

/**
 * Renew the token, single-flight. With `staleToken`, a renewal is only started when that is
 * still the current token (another request may have renewed it already).
 */
export function renewSearchToken(staleToken?: string): Promise<string | null> {
  if (!state) return Promise.resolve(null);
  if (staleToken && state.token !== staleToken) {
    // Renewed meanwhile, or the tab moved to another slug: the newest token for the stale
    // token's own account, never another account's.
    const acc = toState(staleToken)?.acc;
    const latest = acc === undefined ? undefined : latestByAccount.get(acc);
    return Promise.resolve(latest?.token ?? null);
  }
  if (!renewal) {
    if (Date.now() - renewFailedAt < RENEW_BACKOFF_MS) {
      return Promise.reject(new Error("search token renew backing off"));
    }
    const pending = renew(state).finally(() => {
      // initSearchToken may have replaced it with a newer renewal meanwhile.
      if (renewal === pending) renewal = null;
    });
    renewal = pending;
  }
  return renewal;
}

/** Resolves with a token for `current`'s account only, even if the tab moved to another slug meanwhile. */
async function renew(current: TokenState): Promise<string | null> {
  if (refusedToken === current.token) return freshTokenFromPage(current.acc);
  try {
    const nonce = await solvePow(current.jti, current.pow);
    const { token, expires_in } = await postRenew(current.token, nonce);
    const renewed = toState(token, expires_in);
    // Unreadable: keep the current token and back off, rather than stop sending one at all.
    if (!renewed) throw new Error("search token renew returned an unreadable token");
    latestByAccount.set(renewed.acc, renewed);
    if (state === current) state = renewed;
    return renewed.token;
  } catch (error) {
    if (!(error instanceof RenewRefused)) {
      renewFailedAt = Date.now();
      throw error;
    }
    refusedToken = current.token;
    return freshTokenFromPage(current.acc);
  }
}

async function postRenew(
  token: string,
  nonce: string,
): Promise<{ token: string; expires_in: number }> {
  // Retrying the same token is safe: the API hands back the same renewal for a while.
  for (let attempt = 0; ; attempt++) {
    let response: Response | null = null;
    try {
      response = await fetch(`${BASE_API_URL}/search/token/renew`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, nonce }),
      });
    } catch {
      // Network error: retried below.
    }
    if (response?.ok) return response.json();
    // Only the API's own refusal is final. Anything else (404 while a deploy rolls out,
    // 408/429 from a proxy, 5xx, network) is retried and then fails without marking the
    // token as refused.
    if (response?.status === 401) {
      const body = await response.json().catch(() => null);
      if (body?.error === "search_token") {
        // Asked too early (clock skew): the current token is still good, keep using it.
        if (body.reason === "too_early") throw new Error("search token renew too early");
        throw new RenewRefused();
      }
    }
    if (attempt >= RETRY_DELAYS_MS.length) {
      throw new Error(
        `search token renew failed (${response?.status ?? "network"})`,
      );
    }
    await new Promise((resolve) =>
      setTimeout(resolve, RETRY_DELAYS_MS[attempt]),
    );
  }
}

function freshTokenFromPage(acc: string): Promise<string | null> {
  if (!needsFreshToken || Date.now() - lastRefreshAt < REFRESH_COOLDOWN_MS) {
    return Promise.resolve(null);
  }
  lastRefreshAt = Date.now();
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      freshTokenWaiters = freshTokenWaiters.filter((w) => w !== done);
      resolve(null);
    }, FRESH_TOKEN_TIMEOUT_MS);
    const done = (next: TokenState | null) => {
      clearTimeout(timer);
      // The re-rendered page may be another slug's; only this account's token is any use here.
      resolve(next?.acc === acc ? next.token : null);
    };
    freshTokenWaiters.push(done);
    needsFreshToken?.();
  });
}

function solvePow(jti: string, bits: number): Promise<string> {
  if (typeof Worker === "undefined") return solveOnMainThread(jti, bits);
  return new Promise((resolve, reject) => {
    let worker: Worker;
    try {
      worker = new Worker(new URL("./search-token.worker.ts", import.meta.url));
    } catch {
      solveOnMainThread(jti, bits).then(resolve, reject);
      return;
    }
    worker.onmessage = (event: MessageEvent<string | null>) => {
      worker.terminate();
      if (event.data) resolve(event.data);
      else reject(new Error("search token proof-of-work found no nonce"));
    };
    worker.onerror = () => {
      worker.terminate();
      solveOnMainThread(jti, bits).then(resolve, reject);
    };
    worker.postMessage({ jti, bits });
  });
}

// Without Worker support, solve in small chunks and yield between them to keep the UI responsive.
async function solveOnMainThread(jti: string, bits: number): Promise<string> {
  const chunk = 5000;
  for (let start = 0; ; start += chunk) {
    const nonce = solveRange(jti, bits, start, chunk);
    if (nonce) return nonce;
    await new Promise((resolve) => setTimeout(resolve, 0));
  }
}

/** Test hook: forget all module state. */
export function resetSearchTokenForTests() {
  state = null;
  latestByAccount.clear();
  renewal = null;
  needsFreshToken = null;
  freshTokenWaiters = [];
  lastRefreshAt = 0;
  refusedToken = null;
  renewFailedAt = 0;
}
